import { Record, List } from "immutable";
import { IEstimateLineItemOptionData } from "common/records/EstimateLineItemOption";
import { IDocumentData } from "common/records/Document";
import { IImageData } from "common/records/Image";
import { IOpeningEstimationData } from "common/records/OpeningEstimation";
import { DateStringNumber, Nullable } from "common/records";
import { AbleProp } from "common/types";
import { checkDate } from "common/helpers/Date";
import { EstimateRecord, markupMultiplier } from "common/records/Estimate";
import { ProductRecord } from "common/records/Product";
import type { OpeningRecord } from "common/records/Opening";
import { round } from "lodash";

export interface IValidationResultDetailRecord {
  qty_met: number;
  qty_required: number;
  custom_message?: string;
  categories: List<string>;
}

const defaultValidationResultDetailProps: IValidationResultDetailRecord = {
  qty_met: 0,
  qty_required: 0,
  custom_message: undefined,
  categories: List(),
};

export class ValidationResultDetailRecord
  extends Record<IValidationResultDetailRecord>(defaultValidationResultDetailProps)
  implements IValidationResultDetailRecord {}

export interface IValidationResultPhotoRecord {
  custom_message?: string;
}

const defaultValidationResultPhotoProps: IValidationResultPhotoRecord = {
  custom_message: undefined,
};

export class ValidationResultPhotoRecord
  extends Record<IValidationResultPhotoRecord>(defaultValidationResultPhotoProps)
  implements IValidationResultPhotoRecord {}

export interface IValidationResultRecord {
  name: string;
  required: ValidationResultDetailRecord | null;
  optional: ValidationResultDetailRecord | null;
  requiredSelection: ValidationResultDetailRecord | null;
  photo_upload_required: ValidationResultPhotoRecord | null;
}

const defaultValidationResultProps: IValidationResultRecord = {
  name: "",
  required: null,
  optional: null,
  requiredSelection: null,
  photo_upload_required: null,
};

export class ValidationResultRecord
  extends Record<IValidationResultRecord>(defaultValidationResultProps)
  implements IValidationResultRecord {}

export const estimateLineItemFromJSON = (json: Partial<IEstimateLineItemData>): EstimateLineItemRecord => {
  const record: IEstimateLineItemRecord = { ...(json as any) };

  // These fields are client-only (computed locally); never populate them from API data.
  delete (record as any).validated;
  delete (record as any).validation_results;

  if (json.images) {
    record.image_ids = List(json.images.map((i) => i.id));
  }

  if (json.contract_doc_ids) {
    record.contract_doc_ids = List(json.contract_doc_ids);
  }

  if (json.product_image_ids) {
    record.product_image_ids = List(json.product_image_ids);
  }

  if (json.product_image_urls) {
    record.product_image_urls = List(json.product_image_urls);
  }

  if (json.estimate_doc_ids) {
    record.estimate_doc_ids = List(json.estimate_doc_ids);
  }

  if (json.estimate_doc_urls) {
    record.estimate_doc_urls = List(json.estimate_doc_urls);
  }

  if (json.contract_doc_urls) {
    record.contract_doc_urls = List(json.contract_doc_urls);
  }

  if (json.documents) {
    record.document_ids = List(json.documents.map((d) => d.id));
  }

  if (json.options) {
    record.option_ids = List(json.options.map((o) => o.id));
  }

  if (json.opening_estimations) {
    record.opening_estimation_ids = List(json.opening_estimations.map((oe) => oe.id));
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new EstimateLineItemRecord(record);
};

export const calcEstimateLineItemTotals = (lineItem: EstimateLineItemRecord, estimate: EstimateRecord) => {
  const prices = isMarkupable(lineItem)
    ? {
        product_price: round(lineItem.base_product_price * markupMultiplier(estimate.product_markup), 2),
        labor_price: round(lineItem.base_labor_price * markupMultiplier(estimate.labor_markup), 2),
      }
    : {
        product_price: lineItem.base_product_price || 0,
        labor_price: lineItem.base_labor_price || 0,
      };

  const rawPrice = prices.product_price + prices.labor_price;
  // Round `price` to 2 decimals so it matches the server's clean 2-decimal baseline in the
  // isEstimateDirty `.equals()` check (RSF-25841 — the raw float sum, e.g. 484.34000000000003,
  // otherwise reads permanently dirty). `ext_price` stays on the raw sum so totals never drift.
  const price = round(rawPrice, 2);
  const ext_price = round(rawPrice * lineItem.quantity, 2);

  return {
    ...prices,
    price,
    ext_price,
  };
};

export const sumEstimateLineItemTotals = (lineItems: List<Nullable<EstimateLineItemRecord>>) => {
  return lineItems.reduce(
    (totals, lineItemRecord) => {
      if (!lineItemRecord) {
        console.error("Missing required data in redux to sum estimate line item totals", {
          estimateLineItems: lineItems.toJS(),
        });
        return totals;
      }

      if (lineItemRecord._destroy) {
        return totals;
      }

      // subtotal/total/discountable_total read the option-inclusive roll-up `ext_opt_price`
      // (product-only `ext_price` + Σ non-excluded option ext), which the calc chain populates.
      // labor_total/product_total keep reading the separate `option_*` roll-ups (below).
      const extOptPrice = lineItemRecord.ext_opt_price || 0;
      const laborPrice = lineItemRecord.labor_price || 0;
      const productPrice = lineItemRecord.product_price || 0;
      const optionLaborTotal = lineItemRecord.option_labor_total || 0;
      const optionProductTotal = lineItemRecord.option_product_total || 0;
      const quantity = lineItemRecord.quantity || 0;
      const discountableTotal = isDiscountable(lineItemRecord) ? extOptPrice : 0;

      const newSubtotal = totals.subtotal + extOptPrice;
      return {
        subtotal: newSubtotal,
        total: newSubtotal,
        discountable_total: totals.discountable_total + discountableTotal,
        labor_total: totals.labor_total + round(laborPrice * quantity, 2) + optionLaborTotal,
        product_total: totals.product_total + round(productPrice * quantity, 2) + optionProductTotal,
      };
    },
    {
      subtotal: 0,
      total: 0,
      discountable_total: 0,
      labor_total: 0,
      product_total: 0,
    },
  );
};

export const isMarkupable = (estimateLineItem: IEstimateLineItemMarkupable): boolean => {
  return estimateLineItem.markupable !== "no";
};

/**
 * Determines if an item is discountable based on its discountable property.
 * The discountable property is a string AbleProp: "default" | "yes" | "no"
 * - "default" and "yes" are both treated as discountable (returns true)
 * - "no" means not discountable (returns false)
 */
export const isDiscountable = (item: IEstimateLineItemDiscountable): boolean => {
  return item.discountable !== "no";
};

export interface IEstimateLineItemDiscountable {
  discountable: AbleProp;
}

export interface IEstimateLineItemMarkupable {
  markupable: AbleProp;
}

export interface IEstimateLineItemVisible {
  visibility: string;
}

/**
 * Mirrors rsf-app EstimateLineItem.isVisible: a line item is visible when its visibility is
 * "always" or unset, plus "company_only" in company/edit mode (i.e. when not in presentation mode).
 * Every other value — including "never", "a", "b", "ab", and "customer_only" — is hidden in both modes.
 *
 * @param {IEstimateLineItemVisible} item
 * @param {boolean} presentationMode true in customer presentation mode, false in company/edit mode
 * @returns {boolean}
 */
export const isVisible = (item: IEstimateLineItemVisible, presentationMode: boolean): boolean => {
  const v = item.visibility;
  if (presentationMode) {
    return !v || v === "always";
  }
  return !v || v === "always" || v === "company_only";
};
export interface IEstimateLineItemData {
  id: number;
  uuid: string;
  product_uuid: string;
  product_id: Nullable<number>;
  estimate_group_id: number;
  sort_order: number;
  name: string;
  description: string;
  cost: number;
  msrp: number;
  price: number;
  base_product_price: number;
  base_labor_price: number;
  product_price: number;
  labor_price: number;
  option_product_total: number;
  option_labor_total: number;
  visibility: string;
  quantity: number;
  discountable: AbleProp;
  markupable: AbleProp;
  uom: string;
  ext_price: number;
  ext_opt_price: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  _destroy: boolean;
  options: IEstimateLineItemOptionData[];
  documents: IDocumentData[];
  images: IImageData[];
  opening_estimations: IOpeningEstimationData[];
  product_image_ids: number[];
  estimate_doc_ids: number[];
  contract_doc_ids: number[];
  product_image_urls: string[];
  estimate_doc_urls: string[];
  contract_doc_urls: string[];
}

export interface IEstimateLineItemRecord {
  id: number;
  uuid: string;
  product_uuid: string;
  product_id: Nullable<number>;
  estimate_group_id: number;
  sort_order: Nullable<number>;
  name: string;
  description: string;
  cost: number;
  msrp: number;
  price: number;
  base_product_price: number;
  base_labor_price: number;
  product_price: number;
  labor_price: number;
  option_product_total: number;
  option_labor_total: number;
  visibility: string;
  quantity: number;
  discountable: AbleProp;
  markupable: AbleProp;
  uom: string;
  ext_price: number;
  /**
   * Local transient roll-up: product-only `ext_price` + Σ non-excluded option ext prices. Populated
   * by the calc chain (UPDATE_ESTIMATE_LINE_ITEM_TOTALS); never persisted to rsf-api and excluded from
   * the dirty comparison (LINE_ITEM_DERIVED_TOTAL_FIELDS). Group/estimate totals sum this field.
   */
  ext_opt_price: number;
  created_at: Date;
  updated_at: Date;
  _destroy: Nullable<boolean>;
  option_ids: List<number>;
  document_ids: List<number>;
  image_ids: List<number>;
  opening_estimation_ids: List<number>;
  product_image_ids: List<number>;
  estimate_doc_ids: List<number>;
  contract_doc_ids: List<number>;
  product_image_urls: List<string>;
  estimate_doc_urls: List<string>;
  contract_doc_urls: List<string>;
  validated: boolean;
  validation_results: List<ValidationResultRecord>;
}

const defaultEstimateLineItemProps: IEstimateLineItemRecord = {
  id: 0,
  uuid: "",
  product_uuid: "",
  product_id: null,
  estimate_group_id: 0,
  sort_order: null,
  name: "",
  description: "",
  cost: 0,
  msrp: 0,
  price: 0,
  base_product_price: 0,
  base_labor_price: 0,
  product_price: 0,
  labor_price: 0,
  option_product_total: 0,
  option_labor_total: 0,
  visibility: "",
  quantity: 0,
  discountable: "yes",
  markupable: "yes",
  uom: "",
  ext_price: 0,
  ext_opt_price: 0,
  created_at: new Date(),
  updated_at: new Date(),
  _destroy: null,
  option_ids: List<number>(),
  document_ids: List<number>(),
  image_ids: List<number>(),
  opening_estimation_ids: List<number>(),
  product_image_ids: List<number>(),
  estimate_doc_ids: List<number>(),
  contract_doc_ids: List<number>(),
  product_image_urls: List<string>(),
  estimate_doc_urls: List<string>(),
  contract_doc_urls: List<string>(),
  validated: true,
  validation_results: List<ValidationResultRecord>(),
};

export class EstimateLineItemRecord extends Record(defaultEstimateLineItemProps) implements IEstimateLineItemRecord {}

/**
 * The measure an option quantity scales by for a charge type: the resolved first opening's UI for
 * `per_ui`, its area for `per_sqft`, otherwise the passed quantity. Using the first opening is safe
 * because per_ui/per_sqft size groups share identical UI/area by grouping key. The caller resolves
 * the opening (the record stays dumb — see `firstLineItemOpening`); falls back to the passed
 * quantity when there is no opening.
 *
 * @param {Nullable<OpeningRecord>} opening The resolved first opening of the line item
 * @param {string} chargeType The product option's charge_type
 * @param {number} quantity The unscaled quantity
 * @returns {number}
 */
export const openingEstimationMeasurement = (
  opening: Nullable<OpeningRecord>,
  chargeType: string,
  quantity: number,
): number => {
  if (!opening) {
    return quantity;
  }
  switch (chargeType) {
    case "per_ui":
      return opening.ui;
    case "per_sqft":
      return opening.area;
    default:
      return quantity;
  }
};

/** Replaces the segment after the name's last ">" with the size text (rsf-app EstimateLineItem.perName). */
const perName = (name: string, sizeText: string): string =>
  `${name.substring(0, name.lastIndexOf(">") + 1)} ${sizeText}`.trim();

/**
 * Size-based price/name for a `per_ui`/`per_sqft` bracket product (rsf-app windowPriceAdjustment):
 * base prices become `product.base_*_price + product.*_price × (measure − base_measure)` and the
 * name's leaf segment becomes "<measure> UI"/"<measure> SQFT". Returns the line-item field
 * overrides, or null when the product isn't per_ui/per_sqft or there is no opening. The caller
 * resolves the opening (the record stays dumb — see `firstLineItemOpening`).
 *
 * @param {EstimateLineItemRecord} lineItem
 * @param {ProductRecord} product The bracket product on the line item
 * @param {Nullable<OpeningRecord>} opening The resolved first opening of the line item
 * @returns {Nullable<{ name: string; uom: string; base_product_price: number; base_labor_price: number }>}
 */
export const windowPriceAdjustment = (
  lineItem: EstimateLineItemRecord,
  product: ProductRecord,
  opening: Nullable<OpeningRecord>,
): Nullable<{ name: string; uom: string; base_product_price: number; base_labor_price: number }> => {
  if (product.bracket_type !== "per_ui" && product.bracket_type !== "per_sqft") {
    return null;
  }
  if (!opening) {
    return null;
  }

  const measure = product.bracket_type === "per_ui" ? opening.ui : opening.area;
  const factor = round(measure - product.base_measure, 2);
  const unit = product.bracket_type === "per_ui" ? "UI" : "SQFT";

  return {
    name: perName(lineItem.name, `${measure} ${unit}`),
    uom: "ea",
    base_product_price: round(product.base_product_price + product.product_price * factor, 2),
    base_labor_price: round(product.base_labor_price + product.labor_price * factor, 2),
  };
};
